import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useHouseholdMembership } from '../contexts/HouseholdMembershipContext';
import { useSampleData } from '../contexts/SampleDataContext';
import { getAnalyticsSpend } from '../services/analyticsService';
import {
  listTransactions,
  type ApiTransaction,
} from '../services/transactionService';
import type { AnalyticsTrendPoint } from '../types/analytics';
import { shiftMonthKey } from './useSpendBreakdown';

export type TrendDriverKind = 'category' | 'merchant';

export type TrendDriver = {
  id: string;
  kind: TrendDriverKind;
  label: string;
  /** Category slug when kind is category or when merchant belongs to a category filter. */
  categorySlug?: string;
  merchantNormalized?: string;
  currentAmount: number;
  previousAmount: number;
  delta: number;
};

export type TrendPreviewTx = {
  id: string;
  date: string;
  merchant: string;
  amount: number;
  direction: 'debit' | 'credit';
  note?: string;
};

export type TrendMom = {
  focusedKey: string;
  previousKey: string;
  currentAmount: number;
  previousAmount: number;
  currentCount: number;
  previousCount: number;
  deltaAmount: number;
  deltaCount: number;
};

type Options = {
  focusedMonthKey: string | null;
  categoryId: string | null;
  points: AnalyticsTrendPoint[];
  enabled?: boolean;
};

const PREVIEW_LIMIT = 6;
const DRIVER_LIMIT = 5;
const TX_FETCH_LIMIT = 100;

function amountOf(points: AnalyticsTrendPoint[], key: string): number {
  return Math.abs(points.find((p) => p.periodKey === key)?.totalAmount ?? 0);
}

function countOf(points: AnalyticsTrendPoint[], key: string): number {
  return points.find((p) => p.periodKey === key)?.transactionCount ?? 0;
}

function spendOfTx(tx: ApiTransaction): number {
  if (tx.direction === 'credit') return 0;
  return Math.abs(tx.amount);
}

function aggregateMerchants(txs: ApiTransaction[]): Map<string, { label: string; amount: number }> {
  const map = new Map<string, { label: string; amount: number }>();
  for (const tx of txs) {
    const amount = spendOfTx(tx);
    if (amount <= 0) continue;
    const key = tx.merchantNormalized || tx.merchant || 'unknown';
    const prev = map.get(key);
    if (prev) {
      prev.amount += amount;
    } else {
      map.set(key, { label: tx.merchant || key, amount });
    }
  }
  return map;
}

function rankDrivers(
  rows: Array<{
    id: string;
    kind: TrendDriverKind;
    label: string;
    categorySlug?: string;
    merchantNormalized?: string;
    currentAmount: number;
    previousAmount: number;
  }>,
): TrendDriver[] {
  const withDelta = rows.map((row) => ({
    ...row,
    delta: row.currentAmount - row.previousAmount,
  }));
  const increases = withDelta.filter((d) => d.delta > 0.5).sort((a, b) => b.delta - a.delta);
  if (increases.length > 0) return increases.slice(0, DRIVER_LIMIT);
  return withDelta
    .filter((d) => Math.abs(d.delta) > 0.5 || d.currentAmount > 0.5)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) || b.currentAmount - a.currentAmount)
    .slice(0, DRIVER_LIMIT);
}

function mapPreviewTx(tx: ApiTransaction): TrendPreviewTx {
  const dateRaw = tx.date as unknown;
  const dateStr =
    typeof dateRaw === 'string'
      ? dateRaw
      : dateRaw instanceof Date
        ? dateRaw.toISOString()
        : String(dateRaw);
  return {
    id: tx.id,
    date: dateStr.slice(0, 10),
    merchant: tx.merchant,
    amount: Math.abs(tx.amount),
    direction: tx.direction,
    note: tx.description || undefined,
  };
}

/**
 * Month-over-month drivers + transaction preview for a focused Trends month.
 */
export function useSpendTrendInsight(options: Options) {
  const { isAuthenticated, tokens } = useAuth();
  const { isSampleMode } = useSampleData();
  const { view: householdView } = useHouseholdMembership();
  const enabled = options.enabled !== false;
  const focusedKey = options.focusedMonthKey;
  const categorySlug =
    options.categoryId && options.categoryId !== 'all' ? options.categoryId : undefined;

  const useLive = enabled && isAuthenticated && !isSampleMode && !!tokens?.accessToken;

  const mom: TrendMom | null = useMemo(() => {
    if (!focusedKey) return null;
    const previousKey = shiftMonthKey(focusedKey, -1);
    const currentAmount = amountOf(options.points, focusedKey);
    const previousAmount = amountOf(options.points, previousKey);
    const currentCount = countOf(options.points, focusedKey);
    const previousCount = countOf(options.points, previousKey);
    return {
      focusedKey,
      previousKey,
      currentAmount,
      previousAmount,
      currentCount,
      previousCount,
      deltaAmount: currentAmount - previousAmount,
      deltaCount: currentCount - previousCount,
    };
  }, [focusedKey, options.points]);

  const [drivers, setDrivers] = useState<TrendDriver[]>([]);
  const [previewTxs, setPreviewTxs] = useState<TrendPreviewTx[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!useLive || !tokens?.accessToken || !focusedKey || !mom) {
      setDrivers([]);
      setPreviewTxs([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const accessToken = tokens.accessToken;
    const previousKey = mom.previousKey;

    const load = async () => {
      const previewPromise = listTransactions({
        accessToken,
        tab: 'spend',
        period: 'month',
        periodKey: focusedKey,
        categorySlug,
        view: householdView,
        limit: TX_FETCH_LIMIT,
      });

      if (!categorySlug) {
        const [currentSpend, previousSpend, previewRes] = await Promise.all([
          getAnalyticsSpend({
            accessToken,
            period: 'month',
            periodKey: focusedKey,
            view: householdView,
            forceRefresh: true,
          }),
          getAnalyticsSpend({
            accessToken,
            period: 'month',
            periodKey: previousKey,
            view: householdView,
            forceRefresh: true,
          }),
          previewPromise,
        ]);

        if (cancelled) return;

        const prevBySlug = new Map(
          previousSpend.categories.map((c) => [
            c.categorySlug,
            { name: c.categoryName, amount: Math.abs(c.totalAmount) },
          ]),
        );
        const currentBySlug = new Map(
          currentSpend.categories.map((c) => [
            c.categorySlug,
            { name: c.categoryName, amount: Math.abs(c.totalAmount) },
          ]),
        );
        const allSlugs = new Set([...currentBySlug.keys(), ...prevBySlug.keys()]);
        const ranked = rankDrivers(
          [...allSlugs].map((slug) => {
            const current = currentBySlug.get(slug);
            const previous = prevBySlug.get(slug);
            return {
              id: slug,
              kind: 'category' as const,
              label: current?.name ?? previous?.name ?? slug,
              categorySlug: slug,
              currentAmount: current?.amount ?? 0,
              previousAmount: previous?.amount ?? 0,
            };
          }),
        );

        setDrivers(ranked);
        setPreviewTxs(
          [...previewRes.transactions]
            .map(mapPreviewTx)
            .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
            .slice(0, PREVIEW_LIMIT),
        );
        return;
      }

      const [currentTxs, previousTxs] = await Promise.all([
        previewPromise,
        listTransactions({
          accessToken,
          tab: 'spend',
          period: 'month',
          periodKey: previousKey,
          categorySlug,
          view: householdView,
          limit: TX_FETCH_LIMIT,
        }),
      ]);

      if (cancelled) return;

      const currentMap = aggregateMerchants(currentTxs.transactions);
      const previousMap = aggregateMerchants(previousTxs.transactions);
      const ids = new Set([...currentMap.keys(), ...previousMap.keys()]);
      const ranked = rankDrivers(
        [...ids].map((id) => {
          const current = currentMap.get(id);
          const previous = previousMap.get(id);
          return {
            id,
            kind: 'merchant' as const,
            label: current?.label ?? previous?.label ?? id,
            categorySlug,
            merchantNormalized: id,
            currentAmount: current?.amount ?? 0,
            previousAmount: previous?.amount ?? 0,
          };
        }),
      );

      setDrivers(ranked);
      setPreviewTxs(
        [...currentTxs.transactions]
          .map(mapPreviewTx)
          .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
          .slice(0, PREVIEW_LIMIT),
      );
    };

    void load()
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setDrivers([]);
        setPreviewTxs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    useLive,
    tokens?.accessToken,
    focusedKey,
    categorySlug,
    mom?.previousKey,
    householdView,
  ]);

  return {
    mom,
    drivers,
    previewTxs,
    loading,
    error,
    isLive: useLive,
  };
}
